import { Reveal } from '@/components/marketing/site/reveal';

export function CornerTicks({
  className = 'border-line-2',
  size = 5,
  offset = -3,
}: {
  className?: string;
  size?: number;
  offset?: number;
}) {
  const arms = [
    'border-t border-l top-0 left-0',
    'border-t border-r top-0 right-0',
    'border-b border-l bottom-0 left-0',
    'border-b border-r bottom-0 right-0',
  ];

  return (
    <>
      {arms.map((arm) => (
        <span
          key={arm}
          aria-hidden
          className={`pointer-events-none absolute ${arm} ${className}`}
          style={{ width: size, height: size, margin: offset }}
        />
      ))}
    </>
  );
}

export function HudLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-line bg-surface readout text-fg relative inline-flex items-center border px-3 py-1.5">
      {children}
      <CornerTicks />
    </span>
  );
}

export function SectionHeading({
  label,
  lines,
  lead,
  aside,
}: {
  label: string;
  lines: [string, string];
  lead?: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start lg:gap-16">
      <Reveal>
        <HudLabel>{label}</HudLabel>
        <h2 className="display mt-6 text-[2rem] tracking-tight sm:text-5xl lg:text-[3.4rem]">
          <span className="section-heading-line block text-fg">{lines[0]}</span>
          <span className="section-heading-accent text-accent-gradient block">{lines[1]}</span>
        </h2>
      </Reveal>

      {lead || aside ? (
        <Reveal delay={0.1} className="lg:pt-14">
          {lead ? <p className="text-muted leading-relaxed text-pretty">{lead}</p> : null}
          {aside}
        </Reveal>
      ) : null}
    </div>
  );
}

export function HudFrame() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-40">
      <div className="absolute inset-0 flex">
        <div className="hatch border-line/70 hidden flex-1 border-r lg:block" />
        <div className="w-full max-w-[96rem]" />
        <div className="hatch border-line/70 hidden flex-1 border-l lg:block" />
      </div>

      <svg
        viewBox="0 0 1000 600"
        preserveAspectRatio="none"
        className="absolute inset-2 hidden h-[calc(100%_-_1rem)] w-[calc(100%_-_1rem)] text-accent/45 lg:block"
      >
        <path
          d="M10 0H455L465 8H620L630 0H990L1000 10V166L994 172V438L1000 444V590L990 600H620L610 594H390L380 600H10L0 590V444L6 438V172L0 166V10Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.7"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {[
        'top-3 left-3 border-t border-l',
        'top-3 right-3 border-t border-r',
        'bottom-3 left-3 border-b border-l',
        'bottom-3 right-3 border-b border-r',
      ].map((pos) => (
        <span key={pos} className={`border-accent/70 absolute h-5 w-5 ${pos}`} />
      ))}
    </div>
  );
}
